import { isPulished } from '@/functions/handleCourseStatusIcon';
import { Model, Versionable } from '@/models/Model';

export type VersionableModel = Model & Versionable;

export type VersioningSessionOptions<T extends VersionableModel> = {
  createVersion: (id: number) => Promise<T>;
  onVersioned?: (versioned: T, previous: T) => void;
};

export type VersioningSession<T extends VersionableModel> = {
  ensureEditable(): Promise<T>;
  getCurrent(): T;
  setCurrent(model: T): void;
  runEditable<R>(action: (model: T) => Promise<R>): Promise<R>;
};

const createVersioningSession = <T extends VersionableModel>(
  model: T,
  { createVersion, onVersioned }: VersioningSessionOptions<T>,
): VersioningSession<T> => {
  let current = model;
  let versioning: Promise<T> | undefined;
  let pendingPrevious: T | undefined;

  const version = async () => {
    if (!isPulished(current.status)) return current;
    const previous = current;
    versioning ??= createVersion(previous.id).then(
      versioned => {
        current = versioned;
        pendingPrevious = previous;
        return versioned;
      },
      error => {
        versioning = undefined;
        throw error;
      },
    );
    await versioning;
    return current;
  };

  const notify = () => {
    if (!pendingPrevious) return;
    const previous = pendingPrevious;
    pendingPrevious = undefined;
    onVersioned?.(current, previous);
  };

  const ensureEditable = async () => {
    const editable = await version();
    notify();
    return editable;
  };

  const runEditable = async <R>(action: (model: T) => Promise<R>) => {
    const editable = await version();
    try {
      return await action(editable);
    } finally {
      notify();
    }
  };

  return {
    ensureEditable,
    getCurrent: () => current,
    setCurrent: updated => {
      current = updated;
    },
    runEditable,
  };
};

export default createVersioningSession;
